import React from "react";
import Panel from "../../../common/components/panel";
import {
  RmMappingDetailsFragment,
  RmTextFlattenMappingTypeFragment,
  useRmMappingSetPrimarySourceMutation,
  useRmMappingUpdateTextFlattenMutation,
} from "../../../graphql/generated";
import { NotificationContext } from "../../../common/context/notification";
import { DescriptionList } from "../../../common/components/descriptionlist";
import DescriptionListContainer from "../../../common/components/descriptionlistcontainer";
import CommonSwitch from "../../../common/components/switch";
import { EditableSource } from "../mapping-source";

const RMTextFlattenMappingTypeControl: React.FC<{
  mapping: RmMappingDetailsFragment;
  mappingType: RmTextFlattenMappingTypeFragment;
}> = ({ mapping, mappingType }) => {
  const { notifier } = React.useContext(NotificationContext);

  const [{ fetching: updateFetching }, updateTextFlattenMutation] =
    useRmMappingUpdateTextFlattenMutation();
  const [{ fetching: setPrimarySourceFetching }, setPrimarySourceMutation] =
    useRmMappingSetPrimarySourceMutation();

  const onSplitOnCommaToggleChanged = React.useCallback(async () => {
    if (updateFetching) {
      return;
    }
    await updateTextFlattenMutation({
      mappingId: mapping.id,
      splitOnComma: !mappingType.splitOnComma,
      splitOnCommaInArray: mappingType.splitOnCommaInArray ?? false,
    }).then(notifier.notifyGraphql("Split on commas toggled"));
  }, [
    mapping.id,
    mappingType.splitOnComma,
    mappingType.splitOnCommaInArray,
    updateFetching,
    updateTextFlattenMutation,
  ]);

  const onSplitOnCommaInArrayToggleChanged = React.useCallback(async () => {
    if (updateFetching) {
      return;
    }
    await updateTextFlattenMutation({
      mappingId: mapping.id,
      splitOnComma: mappingType.splitOnComma ?? false,
      splitOnCommaInArray: !mappingType.splitOnCommaInArray,
    }).then(notifier.notifyGraphql("Split on commas in array toggled"));
  }, [
    mapping.id,
    mappingType.splitOnComma,
    mappingType.splitOnCommaInArray,
    updateFetching,
    updateTextFlattenMutation,
  ]);

  return (
    <Panel>
      <Panel.Title>Text Flatten Mapping</Panel.Title>
      <Panel.Body>
        <DescriptionList>
          <DescriptionListContainer title="Source">
            <EditableSource
              source={mappingType.source}
              onSubmit={(_, sourceInput) =>
                setPrimarySourceMutation({
                  mappingId: mapping.id,
                  source: sourceInput,
                })
              }
              isSubmitting={setPrimarySourceFetching}
            />
          </DescriptionListContainer>
          <DescriptionListContainer
            title="Split on commas"
            info="Whether or not to treat a comma-separated string as multiple values. Enable this for RETS MLSes and certain API MLSes that return multiple values in a single comma-separated string."
          >
            <CommonSwitch
              label="Split on commas"
              enabled={mappingType.splitOnComma ?? false}
              toggle={onSplitOnCommaToggleChanged}
            />
          </DescriptionListContainer>
          <DescriptionListContainer
            title="Split on commas in array"
            info="Whether or not to split comma-separated values within array items. Enable this when array items contain comma-separated values that should be treated as multiple values."
          >
            <CommonSwitch
              label="Split on commas in array"
              enabled={mappingType.splitOnCommaInArray ?? false}
              toggle={onSplitOnCommaInArrayToggleChanged}
            />
          </DescriptionListContainer>
        </DescriptionList>
      </Panel.Body>
    </Panel>
  );
};

export default RMTextFlattenMappingTypeControl;
